import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import ImageCropper from './ImageCropper'
import RelationshipSelect from '../common/RelationshipSelect'

const { FiX, FiSave, FiUser, FiTrash2, FiCamera, FiAperture, FiPlus, FiSearch, FiUserPlus, FiCalendar, FiFacebook, FiLinkedin, FiTwitter, FiInstagram, FiShare2 } = FiIcons

const capitalizeWords = (str) => {
  if (!str) return ''
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const relationshipGroups = [
  {
    name: "PERSONAL",
    options: [
      { type: 'friend', label: 'Friend', group: 'Personal' }
    ]
  },
  {
    name: "FAMILY",
    options: [
      { type: 'parent', label: 'Parent (Father/Mother)', group: '1st Generation' },
      { type: 'child', label: 'Child (Son/Daughter)', group: '1st Generation' },
      { type: 'sibling', label: 'Sibling (Brother/Sister)', group: '1st Generation' },
      { type: 'spouse', label: 'Spouse (Husband/Wife)', group: '1st Generation' },
      { type: 'ex_spouse', label: 'Ex-Spouse (Ex-Husband/Ex-Wife)', group: '1st Generation' },
      { type: 'partner', label: 'Partner', group: '1st Generation' },
      { type: 'step_parent', label: 'Step-parent (Stepfather/Stepmother)', group: '1st Generation' },
      { type: 'step_child', label: 'Step-child (Stepson/Stepdaughter)', group: '1st Generation' },
      { type: 'step_sibling', label: 'Step-sibling (Stepbrother/Stepsister)', group: '1st Generation' },
      { type: 'half_sibling', label: 'Half-sibling (Half-brother/Half-sister)', group: '1st Generation' },
      { type: 'adoptive_parent', label: 'Adoptive Parent', group: '1st Generation' },
      { type: 'adoptive_child', label: 'Adopted Child', group: '1st Generation' },
      { type: 'grandparent', label: 'Grandparent (Grandfather/Grandmother)', group: '2nd Generation' },
      { type: 'grandchild', label: 'Grandchild (Grandson/Granddaughter)', group: '2nd Generation' },
      { type: 'parental_sibling', label: 'Parental Sibling (Aunt/Uncle)', group: '2nd Generation' },
      { type: 'sibling_child', label: 'Sibling\'s Child (Niece/Nephew)', group: '2nd Generation' },
      { type: 'great_grandparent', label: 'Great-Grandparent', group: '2nd Generation' },
      { type: 'great_grandchild', label: 'Great-Grandchild', group: '2nd Generation' },
      { type: 'great_great_grandparent', label: 'Great-Great-Grandparent', group: '3rd Generation+' },
      { type: 'grandparent_sibling', label: 'Grandparent\'s Sibling (Great-aunt/Great-uncle)', group: '3rd Generation+' },
      { type: 'grandsibling_child', label: 'Grand-sibling\'s Child (Grandniece/Grandnephew)', group: '3rd Generation+' },
      { type: 'cousin', label: 'Cousin (1st Cousin)', group: '3rd Generation+' },
      { type: 'second_cousin', label: 'Second Cousin', group: '3rd Generation+' },
      { type: 'third_cousin', label: 'Third Cousin', group: '3rd Generation+' },
      { type: 'first_cousin_once_removed', label: 'First Cousin Once Removed', group: '3rd Generation+' },
      { type: 'first_cousin_twice_removed', label: 'First Cousin Twice Removed', group: '3rd Generation+' },
      { type: 'second_cousin_once_removed', label: 'Second Cousin Once Removed', group: '3rd Generation+' },
      { type: 'sibling_in_law', label: 'Sibling-in-law (Brother-in-law/Sister-in-law)', group: 'In-Laws' },
      { type: 'parent_in_law', label: 'Parent-in-law (Father-in-law/Mother-in-law)', group: 'In-Laws' },
      { type: 'child_in_law', label: 'Child-in-law (Son-in-law/Daughter-in-law)', group: 'In-Laws' },
      { type: 'cousin_in_law', label: 'Cousin-in-law', group: 'In-Laws' },
      { type: 'sibling_child_in_law', label: 'Niece-in-law / Nephew-in-law', group: 'In-Laws' },
      { type: 'parental_sibling_in_law', label: 'Aunt-in-law / Uncle-in-law', group: 'In-Laws' },
    ]
  },
  {
    name: "PROFESSIONAL",
    options: [
      { type: 'colleague', label: 'Colleague', group: 'Professional' },
      { type: 'boss', label: 'Boss', group: 'Professional' },
      { type: 'employee', label: 'Employee', group: 'Professional' }
    ]
  }
];

const PersonForm = ({
  person,
  people,
  existingRelationships,
  allRelationships,
  onSubmit,
  onCancel,
  onAddNewPerson,
  onOpenPersonForm,
  onViewRelationships,
  initialName = ''
}) => {
  const [formData, setFormData] = useState({
    name: '',
    sex: '',
    nicknames: '',
    notes: '',
    proximity: 'Medium',
    is_family: false,
    dob: '',
    facebook_url: '',
    linkedin_url: '',
    twitter_url: '',
    instagram_url: '',
    photoFile: null,
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [cropperSource, setCropperSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRelationshipForm, setShowRelationshipForm] = useState(false);
  const fileInputRef = useRef(null);

  const [newRelationships, setNewRelationships] = useState([
    { id: 1, relatedPersonId: '', relationshipType: '' },
  ]);

  const [searchTerms, setSearchTerms] = useState({});
  const [searchResults, setSearchResults] = useState({});

  useEffect(() => {
    if (person) {
      setFormData({
        name: person.name || '',
        sex: person.sex || '',
        nicknames: person.nicknames || '',
        notes: person.notes || '',
        proximity: person.proximity || 'Medium',
        is_family: person.is_family || false,
        dob: person.dob || '',
        facebook_url: person.facebook_url || '',
        linkedin_url: person.linkedin_url || '',
        twitter_url: person.twitter_url || '',
        instagram_url: person.instagram_url || '',
        photoFile: null,
      })
      setPhotoPreview(person.photo_url || null)
      setShowRelationshipForm(false)
    } else {
      setFormData({
        name: initialName || '',
        sex: '',
        nicknames: '',
        notes: '',
        proximity: 'Medium',
        is_family: false,
        dob: '',
        facebook_url: '',
        linkedin_url: '',
        twitter_url: '',
        instagram_url: '',
        photoFile: null,
      })
      setPhotoPreview(null)
    }
  }, [person, initialName])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'is_family') {
      setFormData(prev => ({ ...prev, is_family: checked, proximity: checked ? 'Close' : 'Medium' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        setCropperSource(event.target.result);
        setShowImageCropper(true);
      };
      reader.readAsDataURL(file);
      e.target.value = null;
    }
  };

  const handleCameraCapture = () => {
    setCropperSource('camera');
    setShowImageCropper(true);
  };

  const handleCroppedImageSave = (file, previewUrl) => {
    setFormData(prev => ({ ...prev, photoFile: file }));
    setPhotoPreview(previewUrl);
    setShowImageCropper(false);
    setCropperSource(null);
  };

  const handleRemovePhoto = () => {
    setFormData(prev => ({ ...prev, photoFile: null }));
    setPhotoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sex) {
      alert("Please select a sex for this person.");
      return;
    }
    
    setLoading(true);
    try {
      const validRelationships = newRelationships.filter(
        rel => rel.relatedPersonId && rel.relationshipType
      );

      const processedData = {
        ...formData,
        name: capitalizeWords(formData.name.trim()),
        nicknames: formData.nicknames ? formData.nicknames.split(',').map(n => capitalizeWords(n.trim())).join(', ') : ''
      };

      await onSubmit({ ...processedData, newRelationships: validRelationships });
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRelationshipClick = async () => {
    if (person) {
      onCancel();
      if (onViewRelationships) onViewRelationships(person);
      return;
    }

    if (!formData.name.trim()) {
      alert("Please enter a name first.");
      return;
    }
    if (!formData.sex) {
      alert("Please select a sex for this person.");
      return;
    }

    setLoading(true);
    try {
      const processedData = {
        ...formData,
        name: capitalizeWords(formData.name.trim()),
        nicknames: formData.nicknames ? formData.nicknames.split(',').map(n => capitalizeWords(n.trim())).join(', ') : ''
      };

      const saved = await onSubmit({ ...processedData, newRelationships: [] }, true);
      if (saved) {
        onCancel();
        if (onViewRelationships) {
          onViewRelationships(saved);
        }
      }
    } catch (error) {
      console.error("Failed to save and add relationship:", error);
    } finally {
      setLoading(false);
    }
  };

  const availablePeople = people ? people.filter(p => {
    const existingRelIds = new Set(
      (existingRelationships || []).map(rel =>
        rel.person_a_id === (person?.id || 'new') ? rel.person_b_id : rel.person_a_id
      )
    );
    return p.id !== person?.id && !existingRelIds.has(p.id);
  }) : [];

  const handleSearch = (id, query) => {
    setSearchTerms(prev => ({ ...prev, [id]: query }));
    if (!query.trim()) {
      setSearchResults(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    const filtered = availablePeople.filter(
      p => p.name.toLowerCase().includes(query.toLowerCase()) ||
        (p.nicknames && p.nicknames.toLowerCase().includes(query.toLowerCase()))
    );
    setSearchResults(prev => ({ ...prev, [id]: filtered }));
  };

  const handleRelationshipChange = (id, field, value) => {
    const updatedRelationships = newRelationships.map(rel =>
      rel.id === id ? { ...rel, [field]: value } : rel
    );
    setNewRelationships(updatedRelationships);
    
    if (field === 'relatedPersonId') {
      setSearchTerms(prev => ({ ...prev, [id]: '' }));
      setSearchResults(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  return (
    <>
      <motion.div
        key="person-form"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={`bg-white dark:bg-slate-900 rounded-lg shadow-lg p-6 w-full max-h-[90vh] overflow-y-auto transition-all duration-300 ${!person && showRelationshipForm ? 'max-w-4xl' : 'max-w-lg'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white transition-colors duration-300">{person ? 'Edit Person' : 'Add New Person'}</h2>
          <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors" aria-label="Cancel" >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex flex-col md:flex-row md:gap-6">
            <div className="flex-1 min-w-0 space-y-6">
              {/* Profile Section */}
              <div className="flex items-center space-x-6">
                <div className="relative flex-shrink-0">
                  <div className="w-24 h-24 rounded-full bg-gray-100 dark:bg-slate-800 border-2 border-dashed border-gray-300 dark:border-slate-700 flex items-center justify-center overflow-hidden transition-colors duration-300">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <SafeIcon icon={FiUser} className="text-3xl text-gray-400 dark:text-slate-600" />
                    )}
                  </div>
                  <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
                  <div className="absolute -bottom-2 -right-4 flex space-x-2">
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="bg-blue-600 text-white w-8 h-8 flex items-center justify-center rounded-full hover:bg-blue-700 shadow-md transition-colors" >
                      <SafeIcon icon={FiAperture} size={16} />
                    </button>
                    <button type="button" onClick={handleCameraCapture} className="bg-green-600 text-white w-8 h-8 flex items-center justify-center rounded-full hover:bg-green-700 shadow-md transition-colors" >
                      <SafeIcon icon={FiCamera} size={16} />
                    </button>
                  </div>
                </div>

                <div className="flex-grow">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 transition-colors">Full Name</label>
                  <input 
                    type="text" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleChange} 
                    required 
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300" 
                    placeholder="e.g., Jane Doe" 
                  />
                </div>
              </div>

              {photoPreview && (
                <button type="button" onClick={handleRemovePhoto} className="text-xs text-red-500 hover:text-red-700 flex items-center mx-auto transition-colors" >
                  <SafeIcon icon={FiTrash2} className="mr-1" /> Remove Photo
                </button>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 transition-colors">Birthday</label>
                  <div className="relative">
                    <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={14} />
                    <input 
                      type="date" 
                      name="dob" 
                      value={formData.dob} 
                      onChange={handleChange} 
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all duration-300" 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 transition-colors">Sex</label>
                  <select 
                    name="sex" 
                    value={formData.sex} 
                    onChange={handleChange} 
                    required 
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all duration-300" 
                  >
                    <option value="" disabled className="dark:bg-slate-800">Select Sex</option>
                    <option value="male" className="dark:bg-slate-800">Male</option>
                    <option value="female" className="dark:bg-slate-800">Female</option>
                    <option value="other" className="dark:bg-slate-800">Other</option>
                  </select>
                </div>
              </div>

              {/* Social Media Section */}
              <div className="bg-gray-50 dark:bg-slate-800/50 p-4 rounded-xl space-y-4 transition-colors duration-300">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 flex items-center uppercase tracking-wider transition-colors">
                  <SafeIcon icon={FiShare2} className="mr-2 text-blue-600 dark:text-blue-400" /> Social Media
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <SafeIcon icon={FiFacebook} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={16} />
                    <input type="url" name="facebook_url" value={formData.facebook_url} onChange={handleChange} className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all duration-300" placeholder="Facebook URL" />
                  </div>
                  <div className="relative">
                    <SafeIcon icon={FiInstagram} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={16} />
                    <input type="url" name="instagram_url" value={formData.instagram_url} onChange={handleChange} className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all duration-300" placeholder="Instagram URL" />
                  </div>
                  <div className="relative">
                    <SafeIcon icon={FiLinkedin} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={16} />
                    <input type="url" name="linkedin_url" value={formData.linkedin_url} onChange={handleChange} className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all duration-300" placeholder="LinkedIn URL" />
                  </div>
                  <div className="relative">
                    <SafeIcon icon={FiTwitter} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={16} />
                    <input type="url" name="twitter_url" value={formData.twitter_url} onChange={handleChange} className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all duration-300" placeholder="X (Twitter) URL" />
                  </div>
                </div>
              </div>

              {/* Other Info Section */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 transition-colors">Nicknames (comma separated)</label>
                  <input type="text" name="nicknames" value={formData.nicknames} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300" placeholder="e.g., Jenny, JD" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 transition-colors">Notes</label>
                  <textarea name="notes" value={formData.notes} onChange={handleChange} rows="2" className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300" placeholder="Any relevant notes..." />
                </div>
                <div className="flex items-center justify-between">
                  <div className="w-1/2 pr-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 transition-colors">Proximity</label>
                    <select name="proximity" value={formData.proximity} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all duration-300" >
                      <option value="Close" className="dark:bg-slate-800">Close</option>
                      <option value="Medium" className="dark:bg-slate-800">Medium</option>
                      <option value="Far" className="dark:bg-slate-800">Far</option>
                    </select>
                  </div>
                  <div className="flex items-center mt-6">
                    <input type="checkbox" id="is_family" name="is_family" checked={formData.is_family} onChange={handleChange} className="h-4 w-4 text-blue-600 border-gray-300 dark:border-slate-700 rounded focus:ring-blue-500 dark:bg-slate-800 transition-colors" />
                    <label htmlFor="is_family" className="ml-2 block text-sm text-gray-900 dark:text-slate-200 font-medium transition-colors">
                      Is Family Member
                    </label>
                  </div>
                </div>
              </div>

              {/* Relationship Trigger */}
              <div className="border-t border-gray-200 dark:border-slate-700 pt-4 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-md font-medium text-gray-800 dark:text-white transition-colors">Relationships</h3>
                  <button type="button" onClick={handleAddRelationshipClick} className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center font-medium transition-colors" >
                    <SafeIcon icon={FiPlus} className="mr-1" />
                    Add Relationship
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button type="button" onClick={onCancel} className="px-6 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 font-medium transition-colors" >
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center font-medium shadow-md transition-all" >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <SafeIcon icon={FiSave} className="mr-2" />
                  )}
                  {loading ? 'Saving...' : 'Save Person'}
                </button>
              </div>
            </div>

            <AnimatePresence>
              {!person && showRelationshipForm && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="flex-1 md:border-l md:pl-6 mt-6 md:mt-0 min-w-0 border-gray-200 dark:border-slate-700 transition-colors" >
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-lg mb-4 transition-colors">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full transition-colors">
                      <SafeIcon icon={FiUser} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium truncate text-gray-900 dark:text-white transition-colors">{formData.name || 'New Person'}</p>
                      <p className="text-sm text-gray-500 dark:text-slate-400 transition-colors">Connections</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {newRelationships.map((rel, index) => {
                      const currentResults = searchResults[rel.id];
                      const peopleList = (currentResults && currentResults.length > 0) ? currentResults : availablePeople;
                      
                      return (
                        <div key={rel.id} className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg space-y-3 bg-white dark:bg-slate-800 shadow-sm transition-all duration-300">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wider transition-colors">Relationship #{index + 1}</p>
                            {newRelationships.length > 1 && (
                              <button type="button" onClick={() => setNewRelationships(prev => prev.filter(r => r.id !== rel.id))} className="text-red-500 hover:text-red-700 p-1 transition-colors">
                                <SafeIcon icon={FiTrash2} size={16} />
                              </button>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1 transition-colors">Related Person</label>
                            <div className="relative mb-2">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <SafeIcon icon={FiSearch} className="text-gray-400 dark:text-slate-500" size={14} />
                              </div>
                              <input type="text" value={searchTerms[rel.id] || ''} onChange={(e) => handleSearch(rel.id, e.target.value)} placeholder="Search names..." className="w-full pl-9 pr-3 py-1.5 border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all duration-300" />
                            </div>
                            <select 
                              value={rel.relatedPersonId} 
                              onChange={(e) => handleRelationshipChange(rel.id, 'relatedPersonId', e.target.value)} 
                              className="w-full pl-3 pr-8 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all duration-300" 
                              required
                            >
                              <option value="" disabled className="dark:bg-slate-800">Choose person...</option>
                              {peopleList.map(p => (
                                <option key={p.id} value={p.id} className="dark:bg-slate-800">{p.name}</option>
                              ))}
                            </select>
                            {onOpenPersonForm && (
                              <button type="button" onClick={async () => {
                                try {
                                  const searchVal = searchTerms[rel.id] || '';
                                  const newPerson = await onOpenPersonForm(capitalizeWords(searchVal.trim()));
                                  if (newPerson?.id) {
                                    handleRelationshipChange(rel.id, 'relatedPersonId', newPerson.id);
                                  }
                                } catch (error) {
                                  console.error('Failed to add new person:', error);
                                }
                              }} className="mt-2 flex items-center space-x-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline focus:outline-none transition-colors" >
                                <SafeIcon icon={FiUserPlus} size={14} />
                                <span>Add new person</span>
                              </button>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1 transition-colors">Relationship Type</label>
                            <RelationshipSelect value={rel.relationshipType} onChange={(val) => handleRelationshipChange(rel.id, 'relationshipType', val)} relationshipGroups={relationshipGroups} required />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <button type="button" onClick={() => { setNewRelationships(prev => [...prev, { id: Date.now(), relatedPersonId: '', relationshipType: '' }]); }} className="mt-4 w-full text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg py-2.5 flex items-center justify-center font-medium border border-blue-100 dark:border-blue-900/50 transition-all">
                    <SafeIcon icon={FiPlus} className="mr-2" /> Add Another
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </form>
      </motion.div>

      <AnimatePresence>
        {showImageCropper && (
          <ImageCropper imageUrl={cropperSource} onSave={handleCroppedImageSave} onCancel={() => { setShowImageCropper(false); setCropperSource(null); }} isCameraMode={cropperSource === 'camera'} />
        )}
      </AnimatePresence>
    </>
  )
}

export default PersonForm